import I18n from 'I18n';
import ResponsiveRenderer from 'developer-docs-shared-components/components/ResponsiveRenderer';
import useLocale from 'developer-docs-shared-components/hooks/useLocale';
import { getFolderPrefixFromLocale } from 'developer-docs-shared-components/lib/locale/utils';
import { LANDING_PAGE_WIDTH, MOBILE_MENU_BREAKPOINT, mediaQueries } from 'developer-docs-shared-components/utils/mediaQueries';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { ExpandableHeader, ExpandableOpener } from '../../components/ExpandableComponents';
import { CALYPSO_MEDIUM, GYPSUM, OLAF } from 'HubStyleTokens/colors';
import { ECLIPSE } from 'developer-docs-shared-components/config/cssConstants';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIExpandableSection from 'UIComponents/accordion/UIExpandableSection';
import HR from 'UIComponents/elements/HR';
import H5 from 'UIComponents/elements/headings/H5';
import UIIcon from 'UIComponents/icon/UIIcon';
import HubSpotDevLogo from '../../components/HubSpotDevLogo';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILink from 'UIComponents/link/UILink';
import { MANAGE_COOKIES_CLASSNAME } from '../../components/GDPRCompliance';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Registers a `UILink` as external but without an external icon.
 * Allows `rel`, `target`, and accessibility-related attributes to
 * still function.
 */
const externalLinkWithoutIconProps = {
  external: true,
  ExternalIcon: () => null
};
const FooterBackground = styled.footer.withConfig({
  displayName: "MainLandingFooter__FooterBackground",
  componentId: "ybkziy-0"
})(["background-color:", ";color:", ";display:flex;font-size:14px;justify-content:center;"], ECLIPSE, OLAF);
const StyledFooter = styled.div.withConfig({
  displayName: "MainLandingFooter__StyledFooter",
  componentId: "ybkziy-1"
})(["color:", ";font-size:14px;max-width:", "px;padding:64px 0;width:100%;", "{padding-left:12px;padding-right:12px;}"], GYPSUM, LANDING_PAGE_WIDTH, mediaQueries.betweenWidths(MOBILE_MENU_BREAKPOINT, 1150));
const Link = styled(UILink).withConfig({
  displayName: "MainLandingFooter__Link",
  componentId: "ybkziy-2"
})(["color:", ";font-weight:300;&:hover{color:", ";}"], GYPSUM, CALYPSO_MEDIUM);
const LinkColumn = styled.div.withConfig({
  displayName: "MainLandingFooter__LinkColumn",
  componentId: "ybkziy-3"
})(["padding:0 8px;display:inline-flex;flex-direction:column;&:first-child{padding-left:0;}&:last-child{padding-right:0;}"]);
const BottomLink = styled(UILink).withConfig({
  displayName: "MainLandingFooter__BottomLink",
  componentId: "ybkziy-4"
})(["color:", ";font-weight:600;font-size:14px;margin-left:32px;&:hover{text-decoration:none;color:", ";}"], GYPSUM, CALYPSO_MEDIUM);
const DesktopLinks = styled.div.withConfig({
  displayName: "MainLandingFooter__DesktopLinks",
  componentId: "ybkziy-5"
})(["display:inline-flex;align-items:baseline;justify-content:space-between;width:100%;"]);
const MobileBottomLinks = styled(UIFlex).withConfig({
  displayName: "MainLandingFooter__MobileBottomLinks",
  componentId: "ybkziy-6"
})(["width:100%;display:flex;", "{margin-left:0;}"], BottomLink);
const MobileLinks = styled(UIFlex).withConfig({
  displayName: "MainLandingFooter__MobileLinks",
  componentId: "ybkziy-7"
})(["display:flex;width:100%;"]);
const MobileGroups = styled(UIExpandableSection).withConfig({
  displayName: "MainLandingFooter__MobileGroups",
  componentId: "ybkziy-8"
})(["width:100%;padding-bottom:32px;div[role='button']{&:hover{color:", ";}}"], CALYPSO_MEDIUM);
const MobileExpandableHeader = styled(ExpandableHeader).withConfig({
  displayName: "MainLandingFooter__MobileExpandableHeader",
  componentId: "ybkziy-9"
})(["font-size:24px;margin-bottom:16px;padding-left:43px;padding-right:20px;"]);
const LogoContainer = styled(UIFlex).withConfig({
  displayName: "MainLandingFooter__LogoContainer",
  componentId: "ybkziy-10"
})(["text{font-weight:500;}p{font-size:10px;}", "{align-items:center;}"], mediaQueries.belowTablet);
const NoTopMarginHR = styled(HR).withConfig({
  displayName: "MainLandingFooter__NoTopMarginHR",
  componentId: "ybkziy-11"
})(["margin-top:0;"]);
const LI = styled.li.withConfig({
  displayName: "MainLandingFooter__LI",
  componentId: "ybkziy-12"
})(["display:inline-block;", "{&:first-child{margin-left:0;}}", "{margin:0 10px;}"], mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT), mediaQueries.betweenWidths(MOBILE_MENU_BREAKPOINT, 1600));
const UL = styled.ul.withConfig({
  displayName: "MainLandingFooter__UL",
  componentId: "ybkziy-13"
})(["list-style:none;padding:0;margin:0;", ""], ({
  inline
}) => {
  if (!inline) {
    return null;
  }
  return css(["", "{padding:0 8px;background-image:linear-gradient(180deg,", ",", ");background-position:0 50%;background-repeat:no-repeat;background-size:1px 60%;&:first-child{padding-left:0;background:none;}}"], LI, OLAF, OLAF);
});
const sharedBottomLinkProps = {
  elements: {
    BottomLink
  }
};
const CommonLink = ({
  href,
  message
}) => {
  return /*#__PURE__*/_jsx(FormattedJSXMessage, {
    elements: {
      Link
    },
    message: message,
    options: Object.assign({}, externalLinkWithoutIconProps, {
      href
    })
  });
};
const FooterLogo = () => {
  return /*#__PURE__*/_jsxs(LogoContainer, {
    direction: "column",
    title: I18n.text('developer-docs-ui.accessibility.homeLink'),
    children: [/*#__PURE__*/_jsx(HubSpotDevLogo, {
      theme: "on-dark",
      width: 150
    }), /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "developer-docs-ui.footer.copyright",
        options: {
          year: new Date().getFullYear().toString()
        }
      })
    })]
  });
};
const ResourcesLinks = ({
  isDesktop
}) => {
  return /*#__PURE__*/_jsxs(LinkColumn, {
    children: [isDesktop && /*#__PURE__*/_jsx(H5, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "developer-docs-ui.wideFooter.resources.title"
      })
    }), /*#__PURE__*/_jsx(CommonLink, {
      message: "developer-docs-ui.wideFooter.resources.developerHomepage_jsx",
      href: "https://developers.hubspot.com/"
    }), /*#__PURE__*/_jsx(CommonLink, {
      message: "developer-docs-ui.wideFooter.resources.developerTools_jsx",
      href: "https://developers.hubspot.com/developer-tools"
    }), /*#__PURE__*/_jsx(CommonLink, {
      message: "developer-docs-ui.wideFooter.resources.developerChangelog_jsx",
      href: "https://developers.hubspot.com/changelog"
    }), /*#__PURE__*/_jsx(CommonLink, {
      message: "developer-docs-ui.wideFooter.resources.developerCertifications_jsx",
      href: "https://academy.hubspot.com/courses?page=1"
    }), /*#__PURE__*/_jsx(CommonLink, {
      message: "developer-docs-ui.wideFooter.resources.apiTierList_jsx",
      href: "https://developers.hubspot.com/apisbytier"
    })]
  });
};
const CommunityLinks = ({
  isDesktop
}) => {
  return /*#__PURE__*/_jsxs(LinkColumn, {
    children: [isDesktop && /*#__PURE__*/_jsx(H5, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "developer-docs-ui.wideFooter.community.title"
      })
    }), /*#__PURE__*/_jsx(CommonLink, {
      message: "developer-docs-ui.wideFooter.community.developerForum_jsx",
      href: "https://community.hubspot.com/t5/HubSpot-Developers/ct-p/developers"
    }), /*#__PURE__*/_jsx(CommonLink, {
      message: "developer-docs-ui.wideFooter.community.developerBlog_jsx",
      href: "https://developers.hubspot.com/blog"
    }), /*#__PURE__*/_jsx(CommonLink, {
      message: "developer-docs-ui.wideFooter.community.developerNewsletter_jsx",
      href: "https://offers.hubspot.com/developer-newsletter-signup"
    }), /*#__PURE__*/_jsx(CommonLink, {
      message: "developer-docs-ui.wideFooter.community.developerEvents_jsx",
      href: "https://developers.hubspot.com/events"
    })]
  });
};
const MarketplaceLinks = ({
  isDesktop
}) => {
  return /*#__PURE__*/_jsxs(LinkColumn, {
    children: [isDesktop && /*#__PURE__*/_jsx(H5, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "developer-docs-ui.wideFooter.marketplace.title"
      })
    }), /*#__PURE__*/_jsx(CommonLink, {
      message: "developer-docs-ui.wideFooter.marketplace.appEcosystem_jsx",
      href: "https://ecosystem.hubspot.com/marketplace/apps"
    }), /*#__PURE__*/_jsx(CommonLink, {
      message: "developer-docs-ui.wideFooter.marketplace.templateMarketplace_jsx",
      href: "https://ecosystem.hubspot.com/marketplace/templates"
    }), /*#__PURE__*/_jsx(CommonLink, {
      message: "developer-docs-ui.wideFooter.marketplace.appPartner_jsx",
      href: "https://www.hubspot.com/partners/app"
    })]
  });
};
const ProductsLinks = ({
  isDesktop
}) => {
  return /*#__PURE__*/_jsxs(LinkColumn, {
    children: [isDesktop && /*#__PURE__*/_jsx(H5, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "developer-docs-ui.wideFooter.products.title"
      })
    }), /*#__PURE__*/_jsx(CommonLink, {
      message: "developer-docs-ui.wideFooter.products.overview_jsx",
      href: "https://www.hubspot.com/products/get-started"
    }), /*#__PURE__*/_jsx(CommonLink, {
      message: "developer-docs-ui.wideFooter.products.forWordpress_jsx",
      href: "https://ecosystem.hubspot.com/marketplace/apps/marketing/wordpress/wordpress"
    })]
  });
};

/**
 * GTM Cookies link. The link should be empty with no `href` since,
 * when called, the text will be injected and localized based on
 * `<html />` language.
 *
 * @see instructions https://docs.google.com/document/d/1RglGxAPE7jJZn7NoajYWApUI3B9ciWBBrtBTIXrOSWk/edit
 * @see localization https://git.hubteam.com/MarketingWebTeam/webteam-compliance-scripts/blob/dd5674fecf4fb7330f8ae000282b09354e102d83/services/compliance/lang/index.js
 */
const manageCookiesLI = /*#__PURE__*/_jsx(LI, {
  "data-test-id": "gdpr-list-item",
  className: MANAGE_COOKIES_CLASSNAME,
  hidden: true,
  children: /*#__PURE__*/_jsx(BottomLink, {
    href: ""
  })
});
const SocialMediaLinks = () => {
  const sharedIconProps = {
    color: GYPSUM,
    size: 30,
    className: 'm-right-6'
  };
  return /*#__PURE__*/_jsxs(UIFlex, {
    children: [/*#__PURE__*/_jsx(UILink, Object.assign({}, externalLinkWithoutIconProps, {
      href: "https://www.linkedin.com/company/hubspot",
      children: /*#__PURE__*/_jsx(UIIcon, Object.assign({}, sharedIconProps, {
        name: "socialLinkedin"
      }))
    })), /*#__PURE__*/_jsx(UILink, Object.assign({}, externalLinkWithoutIconProps, {
      href: "https://www.youtube.com/c/HubSpotDevelopers",
      children: /*#__PURE__*/_jsx(UIIcon, Object.assign({}, sharedIconProps, {
        name: "socialBlockYoutube"
      }))
    })), /*#__PURE__*/_jsx(UILink, Object.assign({}, externalLinkWithoutIconProps, {
      href: "https://developers.hubspot.com/docs/getting-started/slack/developer-slack",
      children: /*#__PURE__*/_jsx(UIIcon, Object.assign({}, sharedIconProps, {
        name: "socialSlack"
      }))
    })), /*#__PURE__*/_jsx(UILink, Object.assign({}, externalLinkWithoutIconProps, {
      href: "https://github.com/HubSpot",
      children: /*#__PURE__*/_jsx(UIIcon, Object.assign({}, sharedIconProps, {
        name: "github"
      }))
    }))]
  });
};
const DesktopFooterBottom = ({
  legalLink,
  privacyPolicyLink
}) => {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(HR, {}), /*#__PURE__*/_jsxs(UIFlex, {
      children: [/*#__PURE__*/_jsx(UIFlex, {
        children: /*#__PURE__*/_jsx(FooterLogo, {})
      }), /*#__PURE__*/_jsx(SocialMediaLinks, {}), /*#__PURE__*/_jsxs(UL, {
        children: [/*#__PURE__*/_jsx(LI, {
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, Object.assign({}, sharedBottomLinkProps, {
            message: "developer-docs-ui.wideFooter.bottomLinks.legal_jsx",
            options: Object.assign({}, externalLinkWithoutIconProps, {
              href: legalLink
            })
          }))
        }), /*#__PURE__*/_jsx(LI, {
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, Object.assign({}, sharedBottomLinkProps, {
            message: "developer-docs-ui.wideFooter.bottomLinks.privacyPolicy_jsx",
            options: Object.assign({}, externalLinkWithoutIconProps, {
              href: privacyPolicyLink
            })
          }))
        }), manageCookiesLI]
      })]
    })]
  });
};
const MobileFooterBottom = ({
  legalLink,
  privacyPolicyLink
}) => {
  return /*#__PURE__*/_jsxs(MobileBottomLinks, {
    direction: "column",
    align: "center",
    children: [/*#__PURE__*/_jsx(NoTopMarginHR, {}), /*#__PURE__*/_jsx(SocialMediaLinks, {}), /*#__PURE__*/_jsx(HR, {}), /*#__PURE__*/_jsxs(UL, {
      inline: true,
      children: [/*#__PURE__*/_jsx(LI, {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, Object.assign({}, sharedBottomLinkProps, {
          message: "developer-docs-ui.wideFooter.bottomLinks.legal_jsx",
          options: Object.assign({}, externalLinkWithoutIconProps, {
            href: legalLink
          })
        }))
      }), /*#__PURE__*/_jsx(LI, {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, Object.assign({}, sharedBottomLinkProps, {
          message: "developer-docs-ui.wideFooter.bottomLinks.privacyPolicy_jsx",
          options: Object.assign({}, externalLinkWithoutIconProps, {
            href: privacyPolicyLink
          })
        }))
      }), manageCookiesLI]
    }), /*#__PURE__*/_jsx(HR, {}), /*#__PURE__*/_jsx(UIFlex, {
      children: /*#__PURE__*/_jsx(FooterLogo, {})
    })]
  });
};
const MainLandingFooter = () => {
  const locale = useLocale();
  const folderLocalePrefix = useMemo(() => {
    const folderPrefix = getFolderPrefixFromLocale(locale);
    return folderPrefix ? `/${folderPrefix}` : '';
  }, [locale]);
  const legalLink = `https://legal.hubspot.com${folderLocalePrefix}/legal-stuff`;
  const privacyPolicyLink = `https://legal.hubspot.com${folderLocalePrefix}/privacy-policy`;
  return /*#__PURE__*/_jsx(FooterBackground, {
    "data-test-id": "docs-public-main-landing-footer",
    children: /*#__PURE__*/_jsx(StyledFooter, {
      children: /*#__PURE__*/_jsx(ResponsiveRenderer, {
        renderDesktop: () => /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsxs(DesktopLinks, {
            children: [/*#__PURE__*/_jsx(ResourcesLinks, {
              isDesktop: true
            }), /*#__PURE__*/_jsx(CommunityLinks, {
              isDesktop: true
            }), /*#__PURE__*/_jsx(MarketplaceLinks, {
              isDesktop: true
            }), /*#__PURE__*/_jsx(ProductsLinks, {
              isDesktop: true
            })]
          }), /*#__PURE__*/_jsx(DesktopFooterBottom, {
            legalLink: legalLink,
            privacyPolicyLink: privacyPolicyLink
          })]
        }),
        renderMobile: () => /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsxs(MobileLinks, {
            direction: "column",
            children: [/*#__PURE__*/_jsx(MobileGroups, {
              Opener: ExpandableOpener,
              Header: MobileExpandableHeader,
              titleText: I18n.text('developer-docs-ui.wideFooter.resources.title'),
              children: /*#__PURE__*/_jsx(ResourcesLinks, {})
            }), /*#__PURE__*/_jsx(MobileGroups, {
              Opener: ExpandableOpener,
              Header: MobileExpandableHeader,
              titleText: I18n.text('developer-docs-ui.wideFooter.community.title'),
              children: /*#__PURE__*/_jsx(CommunityLinks, {})
            }), /*#__PURE__*/_jsx(MobileGroups, {
              Opener: ExpandableOpener,
              Header: MobileExpandableHeader,
              titleText: I18n.text('developer-docs-ui.wideFooter.marketplace.title'),
              children: /*#__PURE__*/_jsx(MarketplaceLinks, {})
            }), /*#__PURE__*/_jsx(MobileGroups, {
              Opener: ExpandableOpener,
              Header: MobileExpandableHeader,
              titleText: I18n.text('developer-docs-ui.wideFooter.products.title'),
              children: /*#__PURE__*/_jsx(ProductsLinks, {})
            })]
          }), /*#__PURE__*/_jsx(MobileFooterBottom, {
            legalLink: legalLink,
            privacyPolicyLink: privacyPolicyLink
          })]
        })
      })
    })
  });
};
export default MainLandingFooter;